import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { alpha, useTheme } from '@mui/material/styles';
import AdminDashboard from 'layouts/AdminDashboard';
import Container from 'components/Container';
import {
  DemoAccountDTO,
  AccountType,
  DemoStatus,
  ActivityVolume,
  getDemoAccounts,
  deleteDemoAccount,
  enableDemoAccount,
  disableDemoAccount,
  resetDemoAccount,
  triggerGeneration,
  duplicateDemoAccount,
} from './DemoEngineService';
import CreateDemoAccountDialog from './CreateDemoAccountDialog';

const STATUS_CHIP: Record<DemoStatus, { bg: string; color: string; label: string }> = {
  DRAFT:      { bg: '#9E9E9E22', color: '#616161', label: 'Draft' },
  GENERATING: { bg: '#1E88E522', color: '#1565C0', label: 'Generating' },
  ACTIVE:     { bg: '#2E7D3222', color: '#1B5E20', label: 'Active' },
  DISABLED:   { bg: '#C6282822', color: '#B71C1C', label: 'Disabled' },
  ERROR:      { bg: '#F9A82522', color: '#E65100', label: 'Error' },
};

const VOLUME_LABEL: Record<ActivityVolume, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

const fmtDate = (ts: string | null) =>
  ts
    ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const StatusChip = ({ status }: { status: DemoStatus }) => {
  const cfg = STATUS_CHIP[status];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontSize: '0.68rem', height: 22, bgcolor: cfg.bg, color: cfg.color, fontWeight: 600 }}
    />
  );
};

const DemoAccountsPage = (): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [rows, setRows] = useState<DemoAccountDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const [filterType, setFilterType] = useState<AccountType | ''>('');
  const [filterStatus, setFilterStatus] = useState<DemoStatus | ''>('');
  const [filterVolume, setFilterVolume] = useState<ActivityVolume | ''>('');

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DemoAccountDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    getDemoAccounts(
      0,
      100,
      filterType || undefined,
      filterStatus || undefined,
      filterVolume || undefined,
    )
      .then((res) => {
        setRows(res.data.content);
        setTotal(res.data.totalElements);
      })
      .catch(() => setError('Could not load demo accounts.'))
      .finally(() => setLoading(false));
  }, [filterType, filterStatus, filterVolume]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5 s when any account is actively generating
  useEffect(() => {
    const isGenerating = rows.some(
      (r) => r.status === 'GENERATING' || r.latestJob?.status === 'RUNNING',
    );
    if (!isGenerating) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [rows, load]);

  const handleEnable = async (id: number) => {
    try { await enableDemoAccount(id); load(); }
    catch { setActionError('Could not enable demo account.'); }
  };

  const handleDisable = async (id: number) => {
    try { await disableDemoAccount(id); load(); }
    catch { setActionError('Could not disable demo account.'); }
  };

  const handleReset = async (id: number) => {
    try { await resetDemoAccount(id); load(); }
    catch { setActionError('Could not reset demo account.'); }
  };

  const handleGenerate = async (id: number) => {
    try { await triggerGeneration(id); load(); }
    catch (e: any) {
      setActionError(e?.response?.data?.message ?? 'Could not start generation.');
    }
  };

  const handleDuplicate = async (id: number) => {
    try { await duplicateDemoAccount(id); load(); }
    catch { setActionError('Could not duplicate demo account.'); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await deleteDemoAccount(deleteTarget.id); setDeleteTarget(null); load(); }
    catch { setActionError('Could not delete demo account.'); }
    finally { setDeleting(false); }
  };

  return (
    <AdminDashboard>
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.06)}, transparent)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h4" fontWeight={700}>Demo Accounts</Typography>
              {!loading && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {total} demo {total === 1 ? 'account' : 'accounts'}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                Create Demo Account
              </Button>
            </Box>
          </Box>

          {/* Summary stats */}
          {!loading && rows.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total',      value: total,                                                      color: '#1565C0', bg: '#1E88E522' },
                { label: 'Active',     value: rows.filter((r) => r.status === 'ACTIVE').length,           color: '#1B5E20', bg: '#2E7D3222' },
                { label: 'Generating', value: rows.filter((r) => r.status === 'GENERATING').length,       color: '#0277BD', bg: '#29B6F622' },
                { label: 'Error',      value: rows.filter((r) => r.status === 'ERROR').length,            color: '#B71C1C', bg: '#C6282822' },
              ].map(({ label, value, color, bg }) => (
                <Grid item xs={6} sm={3} key={label}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="h4" fontWeight={700} sx={{ color, lineHeight: 1.2 }}>
                        {value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ bgcolor: bg, px: 1, py: 0.25, borderRadius: 1, fontWeight: 600, color }}>
                        {label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          )}

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Account type</InputLabel>
              <Select
                value={filterType}
                label="Account type"
                onChange={(e) => setFilterType(e.target.value as AccountType | '')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PERSONAL">Personal</MenuItem>
                <MenuItem value="FAMILY">Family</MenuItem>
                <MenuItem value="BUSINESS">Business</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value as DemoStatus | '')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="GENERATING">Generating</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="DISABLED">Disabled</MenuItem>
                <MenuItem value="ERROR">Error</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Volume</InputLabel>
              <Select
                value={filterVolume}
                label="Volume"
                onChange={(e) => setFilterVolume(e.target.value as ActivityVolume | '')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Persona</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Volume</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Users</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Last Generated</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          No demo accounts found. Create one to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              #{row.id} · {row.historicalMonths}m history · {row.futureMonths}m forecast
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.accountType}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.68rem', height: 22 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.persona?.name ?? <span style={{ color: '#aaa' }}>None</span>}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{VOLUME_LABEL[row.activityVolume]}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.userCount}</Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={row.status} />
                            {row.latestJob?.status === 'RUNNING' && (
                              <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {fmtDate(row.lastGeneratedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/admin/demo-engine/accounts/${row.id}`)}
                              >
                                View
                              </Button>
                              {(row.status === 'DRAFT' || row.status === 'ACTIVE' || row.status === 'ERROR') && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => handleGenerate(row.id)}
                                >
                                  Generate
                                </Button>
                              )}
                              {row.status === 'ACTIVE' && (
                                <Button size="small" onClick={() => handleDisable(row.id)}>
                                  Disable
                                </Button>
                              )}
                              {row.status === 'DISABLED' && (
                                <Button size="small" color="success" onClick={() => handleEnable(row.id)}>
                                  Enable
                                </Button>
                              )}
                              {row.status === 'ACTIVE' && (
                                <Button size="small" onClick={() => handleReset(row.id)}>
                                  Reset
                                </Button>
                              )}
                              <Button size="small" onClick={() => handleDuplicate(row.id)}>
                                Duplicate
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(row)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Create dialog */}
      {createOpen && (
        <CreateDemoAccountDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete demo account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete <strong>{deleteTarget?.name}</strong> and all its generated data
            (users, transactions, goals, commitments). This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminDashboard>
  );
};

export default DemoAccountsPage;
